import { log, logError } from '../../utils/logging';

/**
 * Wraps paragraph OOXML into the minimal package format required by Word's insertOoxml method.
 * 
 * The insertOoxml method typically expects the same format as getOoxml returns - a full package
 * with namespaces and structure. This utility wraps a paragraph OOXML fragment into that format.
 * 
 * Based on Microsoft Office Open XML format:
 * https://learn.microsoft.com/en-us/office/open-xml/word-processing
 */
export class OoxmlPackageWrapper {
  /**
   * Wraps a paragraph OOXML fragment into a minimal package format for insertOoxml
   * @param paragraphOoxml - The paragraph OOXML (w:p element and contents)
   * @returns Package OOXML suitable for insertOoxml, or empty string if wrapping fails
   */
  static wrapParagraphInPackage(paragraphOoxml: string): string {
    if (!paragraphOoxml || paragraphOoxml.trim().length === 0) {
      logError('[OoxmlPackageWrapper] Empty paragraph OOXML provided');
      return '';
    }

    try {
      log('[OoxmlPackageWrapper] Wrapping paragraph in package format');
      log(`[OoxmlPackageWrapper] Paragraph OOXML length: ${paragraphOoxml.length}`);
      log(`[OoxmlPackageWrapper] Paragraph OOXML preview: ${paragraphOoxml.substring(0, 200)}`);

      // Create the minimal OOXML package structure
      // This follows the format returned by getOoxml() - a pkg:package with parts
      const packageXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">
  <pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
    <pkg:xmlData>
      <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture" xmlns:w14="http://schemas.microsoft.com/office/word/2010/wordml">
        <w:body>
          ${paragraphOoxml}
        </w:body>
      </w:document>
    </pkg:xmlData>
  </pkg:part>
</pkg:package>`;

      log('[OoxmlPackageWrapper] Package created successfully');
      log(`[OoxmlPackageWrapper] Package length: ${packageXml.length}`);
      
      return packageXml;
    } catch (error) {
      logError('[OoxmlPackageWrapper] Error wrapping paragraph in package', error);
      return '';
    }
  }

  /**
   * Validates that the wrapped package is well-formed XML
   * @param packageXml - The package XML to validate
   * @returns true if valid, false otherwise
   */
  static validatePackage(packageXml: string): boolean {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(packageXml, 'text/xml');
      
      // Check for parsing errors
      const parserError = doc.querySelector('parsererror');
      if (parserError) {
        logError('[OoxmlPackageWrapper] Package validation failed - parse error', parserError.textContent);
        return false;
      }
      
      // Check for required structure
      const packageElement = doc.querySelector('package, pkg\\:package');
      if (!packageElement) {
        logError('[OoxmlPackageWrapper] Package validation failed - no package element found');
        return false;
      }
      
      log('[OoxmlPackageWrapper] Package validation passed');
      return true;
    } catch (error) {
      logError('[OoxmlPackageWrapper] Package validation failed with error', error);
      return false;
    }
  }
}
